/**
 * Fatiado de pages/Menu.tsx por responsabilidade (#131, R3).
 */
import { displayTranslation } from './shared.js'
import { useState, type ReactNode } from 'react'
import type { useQueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { products as prodApi, type Category, type Product, type Promotion } from '../../lib/api.js'
import { Button, Switch, Badge, Modal, toast } from '../../components/ui/index.js'
import { cn, formatCents } from '../../lib/utils.js'
import { useI18n } from '../../lib/i18n.js'
import { allergenBadgeState } from '../../lib/allergens.js'
import { attributeState } from '../../lib/attributes.js'
import { isSpecialToday } from '../../lib/daily-specials.js'
import { AllergenBadge } from '../../components/AllergenChips.js'
import { PromotionStatusBadge } from '../../components/PromotionForm.js'
// ─── ProductCard ──────────────────────────────────────────────────────────────

/** #489 M1: interpola {price}/{name} com <bdi dir="auto"> — o valor (nome de
 * variante, preço formatado) pode estar noutra escrita (árabe) e não pode
 * quebrar a direção da frase que o rodeia. */
function interpolateBdi(
  template: string,
  vars: { price: string; name?: string | undefined },
): ReactNode[] {
  return template.split(/(\{price\}|\{name\})/g).map((part, i) => {
    if (part === '{price}') {
      return (
        <bdi key={i} dir="auto">
          {vars.price}
        </bdi>
      )
    }
    if (part === '{name}') {
      return (
        <bdi key={i} dir="auto">
          {vars.name ?? ''}
        </bdi>
      )
    }
    return part
  })
}

export function ProductCard({
  product,
  promo,
  categories,
  lang,
  primaryLocale,
  currency,
  priceLocale = '',
  timezone,
  onEdit,
  onEditPromo,
  qc,
}: {
  product: Product
  /** promoção ativa do produto (mapa de ['promotions','active']), se houver */
  promo: Promotion | null
  categories: Category[]
  lang: string
  primaryLocale: string
  currency: string
  /** #428: locale de formatação Intl (língua base + país do restaurante) */
  priceLocale?: string
  timezone: string | null
  /** #489 M1: com focusPrice abre o editor já focado no campo de preço */
  onEdit: (opts?: { focusPrice?: boolean }) => void
  /** atalho "%" (#205): abre o editor já na seção Promoção */
  onEditPromo: () => void
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const transl = displayTranslation(product.translations, lang, primaryLocale)
  const name = transl?.name ?? '—'
  const desc = transl?.description
  const catName =
    displayTranslation(
      categories.find((c) => c.id === product.category_id)?.translations ?? [],
      lang,
      primaryLocale,
    )?.name ?? '—'

  const toggleMut = useMutation({
    mutationFn: () => prodApi.update(product.id, { is_available: !product.is_available }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['products'] }),
    onError: (e: Error) => toast.error(e.message),
  })

  // #489 M1 (item 3): ligar «No ar» um prato a 0 (ou com variante disponível
  // a 0) pede confirmação — o cliente poderia pedi-lo de graça e a conta e o
  // fecho de caixa ficariam errados. Pausar nunca pergunta; pratos grátis de
  // propósito continuam possíveis (a secundária é afirmação de facto).
  const [zeroLiveWarn, setZeroLiveWarn] = useState(false)
  const zeroVariant = (product.variants ?? []).find((v) => v.is_available && v.price_cents === 0)
  const zeroPriceStr = formatCents(0, currency, priceLocale || lang)

  const handleToggle = () => {
    if (!product.is_available && (product.price_cents === 0 || zeroVariant)) {
      setZeroLiveWarn(true) // sem chamada à API — o prato continua pausado
      return
    }
    toggleMut.mutate()
  }

  // #209: atalho ★ no card — liga «só hoje» no fuso da casa ou desliga
  const specialOn = isSpecialToday(product.daily_special, timezone)
  const specialMut = useMutation({
    mutationFn: () =>
      specialOn
        ? prodApi.setDailySpecial(product.id, { active: false })
        : prodApi.setDailySpecial(product.id, { active: true, mode: 'today' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['products'] }),
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <>
      <div
        data-testid={`catalog-product-${product.id}`}
        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col"
      >
        {(() => {
          const primaryImg = product.images.find((i) => i.is_primary) ?? product.images[0]
          return primaryImg?.url ? (
            <div className="h-36 bg-gray-100 overflow-hidden">
              <img src={primaryImg.url} alt="" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="h-36 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-gray-200" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.644 1.59a.75.75 0 01.712 0l9.75 5.25a.75.75 0 010 1.32l-9.75 5.25a.75.75 0 01-.712 0l-9.75-5.25a.75.75 0 010-1.32l9.75-5.25z" />
                <path d="M3.265 10.602l7.668 4.129a2.25 2.25 0 002.134 0l7.668-4.13 1.37.739a.75.75 0 010 1.32l-9.75 5.25a.75.75 0 01-.71 0l-9.75-5.25a.75.75 0 010-1.32l1.37-.738z" />
              </svg>
            </div>
          )
        })()}
        <div className="p-4 flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-sm font-semibold text-gray-900 leading-snug">{name}</p>
            {/* UX r3 ponto 10: pausado a 0,00 mostra «Sem preço» em vez de
              «0,00 €» (o preço não é real — veio do import sem preço) */}
            <p className="text-sm font-bold text-indigo-700 flex-shrink-0">
              {product.price_cents === 0 && !product.is_available
                ? '—'
                : formatCents(product.price_cents, currency, priceLocale || lang)}
            </p>
          </div>
          {desc && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{desc}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-auto">
            <Badge color="gray" className="text-xs">
              {catName}
            </Badge>
            <Badge color={product.is_available ? 'green' : 'yellow'} className="text-xs">
              {product.is_available ? t('admin.menu.available') : t('admin.menu.unavailable')}
            </Badge>
            {/* #474 UX r2: pausado a 0,00 = veio do import sem preço («PSM»…) —
              o dono percebe porquê sem abrir a edição (#489) */}
            {!product.is_available && product.price_cents === 0 && (
              <span data-testid={`catalog-product-no-price-${product.id}`}>
                <Badge color="yellow" className="text-xs">
                  {t('admin.menu.no_price')}
                </Badge>
              </span>
            )}
            {specialOn && (
              <span data-testid={`catalog-product-special-badge-${product.id}`}>
                <Badge color="indigo" className="text-xs">
                  ★ {t('admin.menu.special')}
                </Badge>
              </span>
            )}
            {promo && (
              <span data-testid={`catalog-product-promo-${product.id}`}>
                <PromotionStatusBadge
                  promo={promo}
                  timezone={timezone}
                  currency={currency}
                  locale={priceLocale || lang}
                  priceLocale={priceLocale}
                />
              </span>
            )}
            <AllergenBadge
              state={allergenBadgeState(product)}
              testId={`catalog-product-allergens-${product.id}`}
            />
            {/* #216: badge só quando há SUGESTÃO por confirmar — sem spam no
              catálogo inteiro para produtos nunca revisados */}
            {attributeState(product) === 'pending' && (
              <span data-testid={`catalog-product-attributes-pending-${product.id}`}>
                <Badge color="yellow" className="text-xs">
                  {t('admin.menu.attributes_pending')}
                </Badge>
              </span>
            )}
            {(product.ingredient_suggestions ?? []).length > 0 && (
              <span data-testid={`catalog-product-ingredients-pending-${product.id}`}>
                <Badge color="yellow" className="text-xs">
                  {t('admin.menu.ingredients_pending')}
                </Badge>
              </span>
            )}
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <Switch
              checked={product.is_available}
              onChange={handleToggle}
              testId={`catalog-product-toggle-${product.id}`}
              label={product.is_available ? t('admin.menu.live') : t('admin.menu.paused')}
              disabled={toggleMut.isPending}
            />
            <div className="flex items-center gap-1">
              {/* atalho ★ (#209): prato do dia HOJE em 1 toque */}
              <Button
                variant="ghost"
                size="sm"
                data-testid={`catalog-product-special-${product.id}`}
                title={t('admin.menu.special_shortcut')}
                aria-label={t('admin.menu.special_shortcut')}
                aria-pressed={specialOn}
                onClick={() => specialMut.mutate()}
                disabled={specialMut.isPending}
                className={cn(
                  'font-bold',
                  specialOn ? 'text-amber-500' : 'text-gray-400 hover:text-amber-500',
                )}
              >
                ★
              </Button>
              {/* atalho "%" (#205): ≤ 2 toques para ligar uma promoção */}
              <Button
                variant="ghost"
                size="sm"
                data-testid={`catalog-product-promo-shortcut-${product.id}`}
                title={t('admin.promo.shortcut_title')}
                aria-label={t('admin.promo.shortcut_title')}
                onClick={onEditPromo}
                className={cn(
                  'font-bold',
                  promo ? 'text-emerald-600' : 'text-gray-400 hover:text-indigo-600',
                )}
              >
                %
              </Button>
              <Button
                variant="ghost"
                size="sm"
                data-testid={`catalog-product-edit-${product.id}`}
                onClick={() => onEdit()}
              >
                {t('admin.common.edit')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* #489 M1 (item 3): aviso ao ligar «No ar» com preço 0 — fechar
        (Esc, ✕, tocar fora) mantém o prato pausado; o interruptor só muda
        com a ação explícita da secundária. Ações empilhadas ≥ 44 px (min-h-11)
        e a toda a largura (cabem em 360 px), primária no topo com o foco. */}
      <Modal
        open={zeroLiveWarn}
        onClose={() => setZeroLiveWarn(false)}
        title={t('admin.menu.zero_price_title')}
        size="sm"
        footer={
          <div className="flex flex-col w-full gap-2">
            <Button
              size="sm"
              autoFocus
              className="w-full min-h-11"
              data-testid={`catalog-zero-price-fix-${product.id}`}
              onClick={() => {
                setZeroLiveWarn(false)
                onEdit({ focusPrice: true })
              }}
            >
              {t('admin.menu.zero_price_fix')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="w-full min-h-11"
              data-testid={`catalog-zero-price-go-live-${product.id}`}
              onClick={() => {
                setZeroLiveWarn(false)
                toggleMut.mutate()
              }}
            >
              {interpolateBdi(t('admin.menu.zero_price_go_live'), { price: zeroPriceStr })}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600" data-testid={`catalog-zero-price-body-${product.id}`}>
          {interpolateBdi(
            zeroVariant ? t('admin.menu.zero_price_option_body') : t('admin.menu.zero_price_body'),
            { price: zeroPriceStr, name: zeroVariant?.name },
          )}
        </p>
      </Modal>
    </>
  )
}
